import { FlightDetails, ConnectionFlight } from "./types";

export type StopsFilter =
  | "none"
  | "one-or-fewer"
  | "one"
  | "two"
  | "three"
  | null;

interface StopsParams {
  Stops?: number;
  IncludeDirect?: boolean;
  IncludeConnections?: boolean;
}

/**
 * Extracts the main city/location name from full airport names.
 * e.g. "DXB - Dubai International Airport" -> "Dubai"
 *      "COK - Cochin International Airport" -> "Cochin"
 *      "Dubai International Airport" -> "Dubai"
 */
export const extractMainName = (fullName: string): string => {
  if (!fullName) return "";
  let name = fullName;
  if (name.includes(" - ")) {
    name = name.split(" - ")[1];
  }
  name = name
    .replace(/\s+(International|Regional|Municipal|Executive|County|Metropolitan|World)?\s*Airport$/i, "")
    .trim();
  return name || fullName;
};

// The API matches on a compact form, so "ek 530" and "Ek530" must both go out
// as EK530.
export const normalizeFlightNumber = (value?: string | null): string =>
  (value ?? "").replace(/\s+/g, "").toUpperCase();

// Translate the UI stops filter into the API's Stops/IncludeDirect/
// IncludeConnections params.
export const mapStopsFilterToParams = (
  stopsFilter: StopsFilter,
): StopsParams => {
  switch (stopsFilter) {
    case "none":
      // Always fetch connections alongside direct flights so the UI can tell
      // "no direct flights, but connections exist" apart from "no flights at
      // all" using the API's total/connectionTotal instead of guessing.
      // Deliberately omits Stops: the API applies it to connections too, so
      // Stops: 0 would zero out connectionTotal even with IncludeConnections
      // true. Direct results are non-stop by definition regardless.
      return { IncludeDirect: true, IncludeConnections: true };
    case "one-or-fewer":
      return { IncludeDirect: false, IncludeConnections: true };
    case "one":
      return { Stops: 1 };
    case "two":
      return { Stops: 2 };
    case "three":
      return { Stops: 3 };
    default:
      return { IncludeDirect: true, IncludeConnections: false };
  }
};

// mapStopsFilterToParams over-fetches on "none" (both direct AND connections,
// so the UI can distinguish "no direct flights" from "no flights at all" —
// see comment above), then filterFlightsByStops/filterConnectionsByStops hide
// connections from the DISPLAYED list client-side. FlightSuggestions must be
// told what's actually shown, not what was over-fetched, or it'll keep
// offering connection-only chips (layover/stops) on a Non-stop-filtered view.
export const mapStopsFilterToDisplayIntent = (
  stopsFilter: StopsFilter,
): StopsParams => {
  switch (stopsFilter) {
    case "none":
      return { IncludeDirect: true, IncludeConnections: false };
    case "one-or-fewer":
      return { IncludeDirect: false, IncludeConnections: true };
    case "one":
      return { IncludeDirect: false, IncludeConnections: true, Stops: 1 };
    case "two":
      return { IncludeDirect: false, IncludeConnections: true, Stops: 2 };
    case "three":
      return { IncludeDirect: false, IncludeConnections: true, Stops: 3 };
    default:
      return { IncludeDirect: true, IncludeConnections: true };
  }
};

// Calculate effective total flight count based on the active stops filter
export const calculateEffectiveTotalFlights = (
  stopsFilter: StopsFilter,
  directTotal?: number,
  connectionTotal?: number,
): number => {
  const d = directTotal ?? 0;
  const c = connectionTotal ?? 0;
  if (stopsFilter === "none") return d;
  if (stopsFilter === "one-or-fewer") return c;
  return d + c;
};

// Filter direct flights list by active stops filter
export const filterFlightsByStops = (
  flightsList: FlightDetails[],
  filter: StopsFilter,
): FlightDetails[] => {
  if (filter === "one-or-fewer") return [];
  return flightsList;
};

// Filter connection flights list by active stops filter
export const filterConnectionsByStops = (
  connList: ConnectionFlight[],
  filter: StopsFilter,
): ConnectionFlight[] => {
  if (filter === "none") return [];
  return connList;
};


const digits = (s: string) => s.replace(/[^0-9]/g, "");

// Stable per-render composite ids so React keys + selection survive paging.
export const withDirectFlightCompositeIds = (
  flights: FlightDetails[],
): FlightDetails[] =>
  flights.map((flight) => {
    const baseId = flight.id ?? `${Date.now()}-${Math.random()}`;
    const scheduleDate = flight.scheduleDate || flight.date;
    const compositeId = scheduleDate
      ? `${baseId}_${digits(scheduleDate)}`
      : String(baseId);
    return { ...flight, compositeId };
  });

export const withConnectionCompositeIds = (
  connections: ConnectionFlight[],
): ConnectionFlight[] =>
  connections.map((connection) => {
    const segmentsWithIds = connection.segments.map((segment) => {
      const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
      const compositeId = segment.departureTime
        ? `${baseId}_${digits(segment.departureTime)}`
        : String(baseId);
      return { ...segment, compositeId };
    });
    // Build the id from ALL segments — two itineraries can share the same first
    // leg (and even the same total duration) but differ in later legs. Keying on
    // only the first segment made those collide, so selecting a shared leg in one
    // card also selected it in every other card that shared that leg.
    const connectionCompositeId = segmentsWithIds.length
      ? `connection_${segmentsWithIds.map((s) => s.compositeId).join("_")}`
      : `connection_${Date.now()}_${Math.random()}`;
    return {
      ...connection,
      segments: segmentsWithIds,
      compositeId: connectionCompositeId,
    };
  });
